import React, { useState } from "react";
import { MapPin, Phone, Mail, Send, Facebook, Instagram } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useReveal } from "../hooks/useReveal";
import { company } from "../mock";
import { toast } from "../hooks/use-toast";

const Contact = () => {
  const { t, lang } = useLang();
  const [ref, visible] = useReveal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    // MOCK: frontend only, no backend persistence
    toast({ title: t.contact.success, description: `${form.name || ""} · ${form.email || ""}` });
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  const info = [
    { icon: MapPin, label: t.contact.addressLabel, value: company.address, href: "https://maps.google.com/?q=Lago+Alberto+320+CDMX" },
    { icon: Phone, label: t.contact.phoneLabel, value: company.phone, href: `tel:${company.phoneRaw}` },
    { icon: Mail, label: t.contact.emailLabel, value: company.email, href: `mailto:${company.email}` }
  ];

  const inputCls = "w-full px-4 py-3 rounded-xl border border-zinc-200 bg-white text-[15px] outline-none focus:border-[var(--zd-red)] focus:ring-2 focus:ring-red-100 transition-all";

  return (
    <section id="contacto" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} text-center max-w-2xl mx-auto mb-14`}>
          <span className={`text-sm font-bold uppercase tracking-wider ${lang === "zh" ? "font-zh" : ""}`} style={{ color: "var(--zd-red)" }}>{t.contact.tag}</span>
          <h2 className={`font-display font-bold text-3xl md:text-4xl mt-3 ${lang === "zh" ? "font-zh" : ""}`} style={{ color: "var(--zd-ink)" }}>{t.contact.title}</h2>
          <div className="h-1 w-16 mx-auto mt-4 rounded-full" style={{ background: "var(--zd-gold)" }} />
          <p className={`mt-4 ${lang === "zh" ? "font-zh" : ""}`} style={{ color: "var(--zd-slate)" }}>{t.contact.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="bg-zinc-50 rounded-3xl p-8 border border-zinc-100">
            <h3 className={`font-display font-bold text-xl mb-6 ${lang === "zh" ? "font-zh" : ""}`} style={{ color: "var(--zd-ink)" }}>{t.contact.infoTitle}</h3>
            <div className="space-y-5">
              {info.map((it, i) => (
                <a key={i} href={it.href} target="_blank" rel="noreferrer" className="flex items-start gap-4 group">
                  <span className="h-11 w-11 rounded-xl grid place-items-center flex-shrink-0 transition-colors" style={{ background: "rgba(110,42,58,.1)" }}>
                    <it.icon className="h-5 w-5" style={{ color: "var(--zd-red)" }} />
                  </span>
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-wide ${lang === "zh" ? "font-zh" : ""}`} style={{ color: "var(--zd-slate)" }}>{it.label}</div>
                    <div className="text-[15px] font-medium mt-0.5 group-hover:text-[var(--zd-red)] transition-colors" style={{ color: "var(--zd-ink)" }}>{it.value}</div>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-zinc-200 flex items-center gap-3">
              <a href={company.social.facebook} target="_blank" rel="noreferrer" className="h-10 w-10 rounded-full grid place-items-center border border-zinc-200 hover:bg-[var(--zd-red)] hover:text-white hover:border-transparent transition-all"><Facebook className="h-4 w-4" /></a>
              <a href={company.social.instagram} target="_blank" rel="noreferrer" className="h-10 w-10 rounded-full grid place-items-center border border-zinc-200 hover:bg-[var(--zd-red)] hover:text-white hover:border-transparent transition-all"><Instagram className="h-4 w-4" /></a>
              <a href={company.social.xiaohongshu} target="_blank" rel="noreferrer" className="h-10 px-4 rounded-full grid place-items-center border border-zinc-200 hover:bg-[var(--zd-red)] hover:text-white hover:border-transparent transition-all font-zh text-sm font-bold">小红书</a>
            </div>

            <div className="mt-6 rounded-2xl overflow-hidden h-52 border border-zinc-200">
              <iframe
                title="map"
                src="https://www.google.com/maps?q=Lago%20Alberto%20320%2C%20Granada%2C%20Miguel%20Hidalgo%2C%2011520%20CDMX&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 border border-zinc-100 shadow-[0_20px_50px_-30px_rgba(20,24,31,.35)]">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${lang === "zh" ? "font-zh" : ""}`} style={{ color: "var(--zd-ink)" }}>{t.contact.name}</label>
                <input name="name" value={form.name} onChange={handleChange} required className={`mt-1.5 ${inputCls}`} />
              </div>
              <div>
                <label className={`text-sm font-medium ${lang === "zh" ? "font-zh" : ""}`} style={{ color: "var(--zd-ink)" }}>{t.contact.phone}</label>
                <input name="phone" value={form.phone} onChange={handleChange} className={`mt-1.5 ${inputCls}`} />
              </div>
            </div>
            <div className="mt-4">
              <label className={`text-sm font-medium ${lang === "zh" ? "font-zh" : ""}`} style={{ color: "var(--zd-ink)" }}>{t.contact.email}</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required className={`mt-1.5 ${inputCls}`} />
            </div>
            <div className="mt-4">
              <label className={`text-sm font-medium ${lang === "zh" ? "font-zh" : ""}`} style={{ color: "var(--zd-ink)" }}>{t.contact.message}</label>
              <textarea name="message" value={form.message} onChange={handleChange} rows={5} required className={`mt-1.5 ${inputCls} resize-none`} />
            </div>
            <button type="submit" className="btn-red mt-6 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold">
              {t.contact.send} <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
